/**
 * Shared Frequently Bought Together (FBT) for Cart Drawer + Cart Page.
 * Listens to cart:change / cart:drawer:render / cart:page:render. No hard dependency on global.js imports.
 */
const DEFAULT_API_URL = "https://api-v1.mamaearth.in/v1/catalog/fbtProducts";
const FBT_CONFETTI_URL =
  "https://cdn.shopify.com/s/files/1/0721/7558/3279/files/confetti-64kb.gif?v=1783327966";

/** @type {Map<string, object>} */
const dynamicCache = new Map();
/** @type {Map<string, object>} */
const productByHandleCache = new Map();

const instances = new Set();
let listenersBound = false;
let cartSyncToken = 0;
let sharedAbort = null;
let sharedRequestId = 0;
/** @type {{ signature: string, promise: Promise<any> } | null} */
let inflightDynamic = null;

const DeliveryState = {
  BELOW_THRESHOLD: "BELOW_THRESHOLD",
  THRESHOLD_REACHED: "THRESHOLD_REACHED",
  CONGRATULATIONS_VISIBLE: "CONGRATULATIONS_VISIBLE",
  POST_CONGRATULATIONS: "POST_CONGRATULATIONS",
};

const deliveryMachine = {
  state: DeliveryState.BELOW_THRESHOLD,
  timerId: null,
  initialized: false,
  lastTotalCents: null,
  lastThresholdCents: null,
};

function safeParseJson(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function toCents(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n);
}

function formatMoneySafe(cents) {
  const value = toCents(cents);
  try {
    if (
      typeof Shopify !== "undefined" &&
      typeof Shopify.formatMoney === "function"
    ) {
      const format =
        window.themeHDN?.settings?.moneyFormat ||
        window.themeHDN?.settings?.money_format ||
        undefined;
      return format
        ? Shopify.formatMoney(value, format)
        : Shopify.formatMoney(value);
    }
  } catch {
    /* ignore */
  }
  const rupees = (value / 100).toFixed(value % 100 === 0 ? 0 : 2);
  return `₹${rupees}`;
}

function replaceAmountPlaceholder(template, amountHtml) {
  if (!template) return "";
  return String(template)
    .replace(/%amount%/gi, amountHtml)
    .replace(/\{\{\s*amount\s*\}\}/gi, amountHtml);
}

function uniqueProductIdsFromCart(cart) {
  const ids = [];
  const seen = new Set();
  const items = Array.isArray(cart?.items) ? cart.items : [];
  for (const item of items) {
    const id = String(item.product_id ?? item.productId ?? "");
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

function buildCartSignature(cart) {
  return uniqueProductIdsFromCart(cart).slice().sort().join(",");
}

function cartProductIdSet(cart) {
  return new Set(uniqueProductIdsFromCart(cart));
}

function validateDynamicPayload(data) {
  if (!data || typeof data !== "object") return null;
  if (String(data.status || "").toUpperCase() !== "OK") return null;
  const products = Array.isArray(data.products) ? data.products : [];
  const details = Array.isArray(data.products_details)
    ? data.products_details
    : [];
  const normalizedDetails = [];
  for (const row of details) {
    if (!row || typeof row !== "object") continue;
    const id = row.id != null ? String(row.id).trim() : "";
    const handle = row.handle != null ? String(row.handle).trim() : "";
    if (!id || !handle) continue;
    if (!/^[a-z0-9][a-z0-9-]*$/i.test(handle)) continue;
    normalizedDetails.push({ id, handle });
  }
  const productIds = products
    .map((id) => (id != null ? String(id).trim() : ""))
    .filter(Boolean);
  return { products: productIds, details: normalizedDetails };
}

function normalizeShopifyProduct(product) {
  if (!product || typeof product !== "object") return null;
  const id = product.id != null ? String(product.id) : "";
  if (!id) return null;
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const availableVariant =
    variants.find((v) => v && v.available) || variants[0] || null;
  const variantId =
    product.variantId != null
      ? Number(product.variantId)
      : availableVariant?.id != null
        ? Number(availableVariant.id)
        : null;
  let price = toCents(
    product.price != null
      ? product.price
      : availableVariant?.price != null
        ? availableVariant.price
        : 0,
  );
  let compareAt = toCents(
    product.compareAtPrice != null
      ? product.compareAtPrice
      : product.compare_at_price != null
        ? product.compare_at_price
        : availableVariant?.compare_at_price != null
          ? availableVariant.compare_at_price
          : 0,
  );
  let discount = Number(product.discount) || 0;
  const metaDiscount = Number(
    product.discountPercentage ?? product.discount_percentage ?? 0,
  );
  // Mirror card-price.liquid: custom.discount_percentage overrides compare-at pricing.
  if (metaDiscount > 0) {
    const salePrice =
      Math.round((price * (100 - metaDiscount)) / 100 / 100) * 100;
    compareAt = price;
    price = salePrice;
    discount = metaDiscount;
  } else if (compareAt > price) {
    discount = discount || discountPercent(price, compareAt);
  } else {
    compareAt = 0;
    discount = 0;
  }

  let image = String(product.image || "");
  if (!image && typeof product.featured_image === "string") {
    image = product.featured_image;
  } else if (!image && product.featured_image?.src) {
    image = product.featured_image.src;
  } else if (!image && Array.isArray(product.images) && product.images[0]) {
    image =
      typeof product.images[0] === "string"
        ? product.images[0]
        : product.images[0].src || "";
  }
  if (image && image.startsWith("//")) image = `https:${image}`;

  return {
    id,
    handle: String(product.handle || ""),
    title: String(product.title || ""),
    type: String(product.type || ""),
    url: product.url
      ? String(product.url)
      : `${window.Shopify?.routes?.root || "/"}products/${product.handle || ""}`,
    image,
    imageAlt: String(product.imageAlt || product.title || ""),
    price,
    compareAtPrice: compareAt > price ? compareAt : 0,
    discount:
      compareAt > price ? discount || discountPercent(price, compareAt) : 0,
    variantId,
    sku: String(product.sku || availableVariant?.sku || ""),
    available: Boolean(
      product.available != null
        ? product.available
        : (availableVariant?.available ?? true),
    ),
  };
}

function normalizeLiquidProduct(raw) {
  if (!raw || typeof raw !== "object") return null;
  const id = raw.id != null ? String(raw.id) : "";
  if (!id) return null;
  let price = toCents(raw.price);
  let compareAt = toCents(raw.compareAtPrice ?? raw.compare_at_price);
  let discount = Number(raw.discount) || 0;
  const metaDiscount = Number(
    raw.discountPercentage ?? raw.discount_percentage ?? 0,
  );
  if (metaDiscount > 0) {
    const salePrice =
      Math.round((price * (100 - metaDiscount)) / 100 / 100) * 100;
    compareAt = price;
    price = salePrice;
    discount = metaDiscount;
  } else if (!(compareAt > price)) {
    compareAt = 0;
    discount = 0;
  } else if (!discount) {
    discount = discountPercent(price, compareAt);
  }
  return {
    id,
    handle: String(raw.handle || ""),
    title: String(raw.title || ""),
    type: String(raw.type || ""),
    url: String(raw.url || ""),
    image: String(raw.image || ""),
    imageAlt: String(raw.imageAlt || raw.title || ""),
    price,
    compareAtPrice: compareAt > price ? compareAt : 0,
    discount: compareAt > price ? discount : 0,
    variantId: raw.variantId != null ? Number(raw.variantId) : null,
    sku: String(raw.sku || ""),
    available: raw.available !== false,
  };
}

function trackFbtAdd(skuName, tabName) {
  const payload = {
    sku_name: skuName || "",
    tab: tabName || "",
  };
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: "fbt_add" }, payload));
  } catch {
    /* ignore */
  }
  try {
    if (typeof window.trackME === "function") {
      window.trackME("fbt_add", payload);
    }
  } catch {
    /* ignore */
  }
}

function discountPercent(price, compareAt) {
  if (!compareAt || compareAt <= price) return 0;
  return Math.round(((compareAt - price) / compareAt) * 100);
}

function formatMoneyPlain(cents) {
  return String(formatMoneySafe(cents) || "").replace(/\.00/g, "");
}

function formatMoneyNoDecimals(cents) {
  const roundedCents = Math.round(toCents(cents) / 100) * 100;
  return formatMoneyPlain(roundedCents);
}

function buildCardPriceHtml(product) {
  const priceText = formatMoneyPlain(product.price);
  const onSale = product.compareAtPrice > product.price;
  const discount =
    product.discount || discountPercent(product.price, product.compareAtPrice);
  let html = `<div class="hdt-price-wrapp fbt-recommendation__price-wrap"><div class="hdt-price__list">`;
  html += `<hdt-price class="hdt-price"><span class="hdt-card-money fbt-recommendation__price">${escapeHtml(priceText)}</span></hdt-price>`;
  if (onSale) {
    const compareText = formatMoneyPlain(product.compareAtPrice);
    html += `<hdt-compare-at-price class="hdt-compare-at-price fbt-recommendation__compare"><span class="hdt-card-money">${escapeHtml(compareText)}</span></hdt-compare-at-price>`;
    if (discount > 0) {
      html += `<hdt-discount class="hdt-discount fbt-recommendation__discount"><span class="hdt-money-discount">${discount}% OFF</span></hdt-discount>`;
    }
  }
  html += `</div></div>`;
  return html;
}

function filterOutCartProducts(products, cartIds) {
  return (products || []).filter(
    (p) => p && p.id && !cartIds.has(String(p.id)),
  );
}

function dedupeProducts(products) {
  const seen = new Set();
  const out = [];
  for (const p of products || []) {
    if (!p?.id || seen.has(p.id)) continue;
    seen.add(p.id);
    out.push(p);
  }
  return out;
}

function extractJsonObject(text) {
  if (!text) return null;
  const trimmed = String(text).trim();
  const parsed = safeParseJson(trimmed);
  if (parsed && typeof parsed === "object") return parsed;
  const start = trimmed.indexOf("{");
  const end = trimmed.lastIndexOf("}");
  if (start === -1 || end <= start) return null;
  return safeParseJson(trimmed.slice(start, end + 1));
}

function extractFbtPriceHtml(text) {
  if (!text) return "";
  let html = "";
  const matched = String(text).match(
    /<!--fbt-price:start-->([\s\S]*?)<!--fbt-price:end-->/,
  );
  if (matched) html = matched[1].trim();
  if (!html) {
    try {
      const doc = new DOMParser().parseFromString(String(text), "text/html");
      const root = doc.querySelector("[data-fbt-price-root]");
      if (root) html = root.innerHTML.trim();
    } catch {
      /* ignore */
    }
  }
  // Drop card-price {% style %} so FBT card typography stays unchanged.
  return html.replace(/<style[\s\S]*?<\/style>/gi, "").trim();
}

async function fetchProductByHandle(handle, signal) {
  const key = String(handle || "").trim();
  if (!key) return null;
  if (productByHandleCache.has(key)) return productByHandleCache.get(key);

  const root = window.Shopify?.routes?.root || "/";
  try {
    const res = await fetch(`${root}products/${encodeURIComponent(key)}.js`, {
      signal,
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!res.ok) return null;
    const data = extractJsonObject(await res.text());
    const normalized = normalizeShopifyProduct(data);
    if (!normalized) return null;

    // Use card-price.liquid output for sale/compare strikethrough (metafield-aware).
    try {
      const priceRes = await fetch(
        `${root}products/${encodeURIComponent(key)}?view=fbt-price`,
        { signal, credentials: "same-origin" },
      );
      if (priceRes.ok) {
        const priceHtml = extractFbtPriceHtml(await priceRes.text());
        if (priceHtml) normalized.priceHtml = priceHtml;
      }
    } catch {
      /* optional; fall back to buildCardPriceHtml */
    }

    productByHandleCache.set(key, normalized);
    return normalized;
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    return null;
  }
}

async function resolveProductsFromDetails(details, cartIds, signal) {
  const tasks = [];
  for (const row of details) {
    if (!row?.handle) continue;
    if (cartIds.has(String(row.id))) continue;
    tasks.push(
      fetchProductByHandle(row.handle, signal).then((product) => {
        if (!product) return null;
        if (cartIds.has(String(product.id))) return null;
        return product;
      }),
    );
  }
  const results = await Promise.all(tasks);
  return dedupeProducts(results.filter(Boolean));
}

async function fetchDynamicFbt(apiUrl, productIds, signal) {
  const idsParam = productIds.join(",");
  if (!idsParam) return { products: [], details: [] };

  const url = new URL(apiUrl || DEFAULT_API_URL);
  url.searchParams.set("productIds", idsParam);

  const res = await fetch(url.toString(), {
    method: "GET",
    signal,
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`Dynamic FBT HTTP ${res.status}`);
  const json = await res.json();
  const validated = validateDynamicPayload(json);
  if (!validated) throw new Error("Dynamic FBT invalid payload");
  return validated;
}

function clearDeliveryTimer() {
  if (deliveryMachine.timerId != null) {
    clearTimeout(deliveryMachine.timerId);
    deliveryMachine.timerId = null;
  }
}

function setDeliveryState(next) {
  deliveryMachine.state = next;
  for (const instance of instances) {
    instance.renderDeliveryHeader();
  }
}

function updateDeliveryMachine(
  totalCents,
  thresholdCents,
  durationMs,
  isInitial,
) {
  deliveryMachine.lastTotalCents = totalCents;
  deliveryMachine.lastThresholdCents = thresholdCents;

  const above = totalCents >= thresholdCents;
  const prev = deliveryMachine.state;
  const duration = Math.max(0, durationMs || 3000);

  const startCongratsTimer = () => {
    clearDeliveryTimer();
    deliveryMachine.timerId = setTimeout(() => {
      deliveryMachine.timerId = null;
      if (deliveryMachine.state === DeliveryState.CONGRATULATIONS_VISIBLE) {
        // Always replace congrats with post icon/title/subtext design.
        setDeliveryState(DeliveryState.POST_CONGRATULATIONS);
      }
    }, duration);
  };

  if (isInitial && !deliveryMachine.initialized) {
    deliveryMachine.initialized = true;
    clearDeliveryTimer();
    if (above) {
      setDeliveryState(DeliveryState.CONGRATULATIONS_VISIBLE);
      startCongratsTimer();
    } else {
      setDeliveryState(DeliveryState.BELOW_THRESHOLD);
    }
    return;
  }

  // After drawer DOM replace: if we were celebrating but lost the timer, finish to post.
  if (
    above &&
    prev === DeliveryState.CONGRATULATIONS_VISIBLE &&
    deliveryMachine.timerId == null
  ) {
    setDeliveryState(DeliveryState.POST_CONGRATULATIONS);
    return;
  }

  if (!above) {
    clearDeliveryTimer();
    if (prev !== DeliveryState.BELOW_THRESHOLD) {
      setDeliveryState(DeliveryState.BELOW_THRESHOLD);
    } else {
      for (const instance of instances) instance.renderDeliveryHeader();
    }
    return;
  }

  if (
    prev === DeliveryState.BELOW_THRESHOLD ||
    prev === DeliveryState.THRESHOLD_REACHED
  ) {
    setDeliveryState(DeliveryState.CONGRATULATIONS_VISIBLE);
    startCongratsTimer();
    return;
  }

  for (const instance of instances) instance.renderDeliveryHeader();
}

async function addVariantToCart(variantId, fromElement) {
  const loaderScope = window.HdtCartLoader?.getScope?.(fromElement) || null;
  document.dispatchEvent(
    new CustomEvent("theme:loading:doing", {
      bubbles: true,
      detail: { scope: loaderScope, fromElement },
    }),
  );

  try {
    const sections = [];
    document.dispatchEvent(
      new CustomEvent("cart:push:section.id", {
        bubbles: true,
        detail: { sections },
      }),
    );

    const body = new FormData();
    body.set("id", String(variantId));
    body.set("quantity", "1");
    if (sections.length) body.set("sections", sections.join(","));

    const root = window.Shopify?.routes?.root || "/";
    const res = await fetch(`${root}cart/add.js`, {
      method: "POST",
      body,
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(
        json?.description || json?.message || "Unable to add to cart",
      );
    }

    let cart = null;
    if (typeof window.createCartPromise === "function") {
      cart = await window.createCartPromise(true);
    } else {
      const cartRes = await fetch(`${root}cart.js`);
      cart = await cartRes.json();
    }
    if (cart && json?.sections) cart.sections = json.sections;

    // Cart page form redirects on baseEvent "variant:add". Use line-item:change there so
    // the main cart re-renders in place (same path as qty updates). Drawer keeps variant:add.
    const onCartPage = Boolean(
      document.querySelector("wrapp-hdt-cart-page-form"),
    );
    const baseEvent = onCartPage ? "line-item:change" : "variant:add";

    document.dispatchEvent(
      new CustomEvent("variant:add", {
        bubbles: true,
        detail: { items: json?.items ? json.items : [json], cart },
      }),
    );
    document.dispatchEvent(
      new CustomEvent("cart:change", {
        bubbles: true,
        detail: { baseEvent, cart },
      }),
    );

    if (
      typeof Shopify !== "undefined" &&
      typeof Shopify.cartNotification === "function"
    ) {
      try {
        // On cart page, toast is enough; drawer notification path is for non-cart contexts.
        Shopify.cartNotification();
      } catch {
        /* ignore */
      }
    }

    return cart;
  } finally {
    document.dispatchEvent(
      new CustomEvent("theme:loading:done", {
        bubbles: true,
        detail: { scope: loaderScope, fromElement },
      }),
    );
  }
}

class FbtRecommendation {
  constructor(root) {
    this.root = root;
    this.config = {};
    this.manualProducts = [];
    this.dynamicProducts = [];
    this.activeTab = "dynamic";
    this.userPickedTab = false;
    this.loadingDynamic = false;
    this.destroyed = false;
    this.lastSignature = "";
    this.lastCart = { items: [], total_price: 0 };
    this.bound = {
      onTabClick: this.onTabClick.bind(this),
      onTabKeydown: this.onTabKeydown.bind(this),
      onProductsClick: this.onProductsClick.bind(this),
    };
  }

  init() {
    if (this.root.dataset.fbtReady === "1") return;
    this.root.dataset.fbtReady = "1";

    const configEl = this.root.querySelector("[data-fbt-config]");
    const config = configEl ? safeParseJson(configEl.textContent) : null;
    if (!config) {
      this.hide();
      return;
    }
    this.config = config;
    this.manualProducts = dedupeProducts(
      (config.manualProducts || []).map(normalizeLiquidProduct).filter(Boolean),
    );
    if (this.getMode() === "manual") {
      this.activeTab = "manual";
    }

    this.cacheDom();
    this.applyVisibilityChrome();
    this.root
      .querySelectorAll("[data-fbt-tab]")
      .forEach((btn) => btn.addEventListener("click", this.bound.onTabClick));
    this.tabsEl?.addEventListener("keydown", this.bound.onTabKeydown);
    this.productsEl?.addEventListener("click", this.bound.onProductsClick);

    instances.add(this);

    const initialCart = config.cart || { items: [], total_price: 0 };
    this.lastCart = initialCart;
    this.lastSignature = "";
    updateDeliveryMachine(
      toCents(initialCart.total_price),
      toCents(config.freeDeliveryThresholdCents),
      Number(config.congratsDurationMs) || 3000,
      true,
    );
    this.renderDeliveryHeader();

    // Show shell immediately (header / loading) while Dynamic FBT loads.
    this.evaluateVisibility(initialCart);

    this.refreshFromCart(initialCart, { isInitial: true }).catch(() => {
      this.evaluateVisibility(this.lastCart);
    });
  }

  cacheDom() {
    this.headerEl = this.root.querySelector("[data-fbt-header]");
    this.panelBelow = this.root.querySelector('[data-fbt-panel="below"]');
    this.panelCongrats = this.root.querySelector('[data-fbt-panel="congrats"]');
    this.panelPost = this.root.querySelector('[data-fbt-panel="post"]');
    this.titleBelow = this.root.querySelector("[data-fbt-title-below]");
    this.subtextBelow = this.root.querySelector("[data-fbt-subtext-below]");
    this.titlePost = this.root.querySelector("[data-fbt-title-post]");
    this.subtextPost = this.root.querySelector("[data-fbt-subtext-post]");
    this.congratsTitle = this.root.querySelector("[data-fbt-congrats-title]");
    this.congratsMessage = this.root.querySelector(
      "[data-fbt-congrats-message]",
    );
    this.tabsEl = this.root.querySelector("[data-fbt-tabs]");
    this.tabDynamic = this.root.querySelector('[data-fbt-tab="dynamic"]');
    this.tabManual = this.root.querySelector('[data-fbt-tab="manual"]');
    this.sourceHeadingEl = this.root.querySelector("[data-fbt-source-heading]");
    this.sourceHeadingTextEl = this.root.querySelector(
      "[data-fbt-source-heading-text]",
    );
    this.confettiEl = this.root.querySelector("[data-fbt-confetti]");
    this.confettiImg = this.root.querySelector("[data-fbt-confetti-img]");
    this.productsEl = this.root.querySelector("[data-fbt-products]");
    this.trackEl = this.root.querySelector("[data-fbt-track]");
    this.manualTemplate = this.root.querySelector("[data-fbt-manual-template]");
  }

  applyVisibilityChrome() {
    if (this.config.showFreeDeliveryHeader === false && this.headerEl) {
      this.headerEl.hidden = true;
    }
  }

  destroy() {
    this.destroyed = true;
    this.root
      .querySelectorAll("[data-fbt-tab]")
      .forEach((btn) =>
        btn.removeEventListener("click", this.bound.onTabClick),
      );
    this.tabsEl?.removeEventListener("keydown", this.bound.onTabKeydown);
    this.productsEl?.removeEventListener("click", this.bound.onProductsClick);
    instances.delete(this);
    if (this.root.isConnected) this.root.dataset.fbtReady = "";
  }

  hide() {
    this.root.classList.remove("is-visible");
    this.root.hidden = true;
    this.root.setAttribute("aria-hidden", "true");
  }

  show() {
    this.root.hidden = false;
    this.root.classList.add("is-visible");
    this.root.setAttribute("aria-hidden", "false");
  }

  getMode() {
    return String(this.config.recommendationSource || "both").toLowerCase();
  }

  getManualFiltered(cart) {
    const cartIds = cartProductIdSet(cart);
    return filterOutCartProducts(this.manualProducts, cartIds).filter(
      (p) => p.available !== false,
    );
  }

  getDynamicFiltered(cart) {
    const cartIds = cartProductIdSet(cart);
    return filterOutCartProducts(this.dynamicProducts, cartIds).filter(
      (p) => p.available !== false,
    );
  }

  async refreshFromCart(
    cart,
    { isInitial = false, forceDynamic = false } = {},
  ) {
    if (this.destroyed || !this.root.isConnected) return;
    this.lastCart = cart || this.lastCart;

    const mode = this.getMode();
    const signature = buildCartSignature(cart);
    const threshold = toCents(this.config.freeDeliveryThresholdCents);
    const duration = Number(this.config.congratsDurationMs) || 3000;

    updateDeliveryMachine(
      toCents(cart.total_price),
      threshold,
      duration,
      isInitial,
    );
    this.renderDeliveryHeader();

    const needsDynamic = mode === "dynamic" || mode === "both";
    const needsManual = mode === "manual" || mode === "both";

    if (needsManual && Array.isArray(cart.__fbtManualProducts)) {
      this.manualProducts = dedupeProducts(
        cart.__fbtManualProducts.map(normalizeLiquidProduct).filter(Boolean),
      );
    }

    const signatureChanged = signature !== this.lastSignature;
    this.lastSignature = signature;

    if (needsDynamic) {
      if (!signature) {
        this.dynamicProducts = [];
      } else if (
        !forceDynamic &&
        !signatureChanged &&
        dynamicCache.has(signature)
      ) {
        await this.hydrateDynamicFromCache(dynamicCache.get(signature), cart);
      } else if (
        forceDynamic ||
        signatureChanged ||
        !dynamicCache.has(signature)
      ) {
        await this.loadDynamic(signature, cart);
      }
    } else {
      this.dynamicProducts = [];
    }

    if (!needsManual) this.manualProducts = [];

    this.evaluateVisibility(cart);
  }

  async hydrateDynamicFromCache(cached, cart) {
    const cartIds = cartProductIdSet(cart);
    if (cached?.resolvedProducts) {
      this.dynamicProducts = filterOutCartProducts(
        cached.resolvedProducts,
        cartIds,
      );
      return;
    }
    this.dynamicProducts = await resolveProductsFromDetails(
      cached?.details || [],
      cartIds,
    );
  }

  async loadDynamic(signature, cart) {
    const requestId = ++sharedRequestId;
    this.loadingDynamic = true;
    this.evaluateVisibility(cart);
    this.renderLoadingIfNeeded();

    try {
      if (dynamicCache.get(signature)?.resolvedProducts) {
        await this.hydrateDynamicFromCache(dynamicCache.get(signature), cart);
        return;
      }

      if (inflightDynamic && inflightDynamic.signature === signature) {
        const shared = await inflightDynamic.promise;
        if (requestId !== sharedRequestId) return;
        if (shared) {
          dynamicCache.set(signature, shared);
          await this.hydrateDynamicFromCache(shared, cart);
        } else {
          this.dynamicProducts = [];
        }
        return;
      }

      if (sharedAbort) {
        try {
          sharedAbort.abort();
        } catch {
          /* ignore */
        }
      }
      sharedAbort =
        typeof AbortController !== "undefined" ? new AbortController() : null;
      const signal = sharedAbort?.signal;
      const productIds = uniqueProductIdsFromCart(cart);

      const promise = (async () => {
        const payload = await fetchDynamicFbt(
          this.config.apiUrl,
          productIds,
          signal,
        );
        const cartIds = cartProductIdSet(cart);
        // Prefer API order from products_details; fall back to products[] order.
        let details = payload.details;
        if (!details.length && payload.products.length) {
          // IDs only — cannot hydrate without handles
          details = [];
        }
        const resolved = await resolveProductsFromDetails(
          details,
          cartIds,
          signal,
        );
        return {
          products: payload.products,
          details,
          resolvedProducts: resolved,
        };
      })();

      inflightDynamic = { signature, promise };
      let result = null;
      try {
        result = await promise;
      } finally {
        if (inflightDynamic?.promise === promise) inflightDynamic = null;
      }

      if (requestId !== sharedRequestId) return;
      if (!result) {
        this.dynamicProducts = [];
        return;
      }
      dynamicCache.set(signature, result);
      this.dynamicProducts = result.resolvedProducts || [];
    } catch (err) {
      if (err?.name === "AbortError") return;
      if (requestId !== sharedRequestId) return;
      console.warn("[FBT] Dynamic recommendations failed", err);
      if (dynamicCache.has(signature)) {
        await this.hydrateDynamicFromCache(dynamicCache.get(signature), cart);
      } else {
        this.dynamicProducts = [];
      }
    } finally {
      this.loadingDynamic = false;
    }
  }

  evaluateVisibility(cart) {
    if (this.destroyed) return;
    const mode = this.getMode();
    const dynamicList = this.getDynamicFiltered(cart || { items: [] });
    const manualList = this.getManualFiltered(cart || { items: [] });
    const manualFromLiquid =
      Number(this.config.manualCount || this.root.dataset.fbtManualCount || 0) >
        0 || Boolean(this.manualTemplate?.content?.childElementCount);

    const hasDynamic = dynamicList.length > 0;
    const hasManual = manualList.length > 0 || manualFromLiquid;

    let showDynamicTab = false;
    let showManualTab = false;

    if (mode === "dynamic") {
      showDynamicTab = hasDynamic || this.loadingDynamic;
      showManualTab = false;
    } else if (mode === "manual") {
      showDynamicTab = false;
      showManualTab = hasManual;
    } else {
      showDynamicTab = hasDynamic || this.loadingDynamic;
      showManualTab = hasManual;
    }

    // The upsell must not remain visible just because its delivery header is
    // enabled or while a dynamic request has not returned any products yet.
    const hasAnyProducts = hasDynamic || hasManual;
    if (!hasAnyProducts) {
      this.hide();
      return;
    }

    if (mode === "manual") {
      this.activeTab = "manual";
    } else if (mode === "dynamic") {
      this.activeTab = "dynamic";
    } else if (showDynamicTab && showManualTab) {
      // Both sources available — keep Dynamic active unless the shopper switched tabs.
      if (!this.userPickedTab) this.activeTab = "dynamic";
    } else if (
      this.activeTab === "dynamic" &&
      !showDynamicTab &&
      showManualTab
    ) {
      this.activeTab = "manual";
    } else if (
      this.activeTab === "manual" &&
      !showManualTab &&
      showDynamicTab
    ) {
      this.activeTab = "dynamic";
    }

    this.show();
    this.renderTabs(showDynamicTab, showManualTab);
    this.renderActiveProducts(cart);

    if (this.productsEl) {
      this.productsEl.hidden =
        !showDynamicTab && !showManualTab && !this.loadingDynamic;
    }
  }

  renderTabs(showDynamicTab, showManualTab) {
    const bothVisible = showDynamicTab && showManualTab;
    const singleVisible =
      (showDynamicTab && !showManualTab) || (!showDynamicTab && showManualTab);

    if (this.tabsEl) {
      this.tabsEl.classList.toggle("is-visible", bothVisible);
      this.tabsEl.hidden = !bothVisible;
    }

    if (this.tabDynamic) {
      this.tabDynamic.hidden = !showDynamicTab;
      this.tabDynamic.setAttribute(
        "aria-selected",
        this.activeTab === "dynamic" ? "true" : "false",
      );
      this.tabDynamic.tabIndex = this.activeTab === "dynamic" ? 0 : -1;
    }
    if (this.tabManual) {
      this.tabManual.hidden = !showManualTab;
      this.tabManual.setAttribute(
        "aria-selected",
        this.activeTab === "manual" ? "true" : "false",
      );
      this.tabManual.tabIndex = this.activeTab === "manual" ? 0 : -1;
    }

    if (this.sourceHeadingEl) {
      this.sourceHeadingEl.classList.toggle("is-visible", singleVisible);
      this.sourceHeadingEl.hidden = !singleVisible;
      if (singleVisible && this.sourceHeadingTextEl) {
        const label = showManualTab
          ? this.config.manualTabLabel || "Buy Again"
          : this.config.dynamicTabLabel || "Frequently bought together";
        this.sourceHeadingTextEl.textContent = label;
      }
    }

    if (this.productsEl) {
      let labelledBy = "";
      if (singleVisible && this.sourceHeadingEl?.id) {
        labelledBy = this.sourceHeadingEl.id;
      } else if (this.activeTab === "manual" && this.tabManual) {
        labelledBy = this.tabManual.id;
      } else if (this.tabDynamic) {
        labelledBy = this.tabDynamic.id;
      }
      if (labelledBy)
        this.productsEl.setAttribute("aria-labelledby", labelledBy);
    }
  }

  renderDeliveryHeader() {
    if (!this.headerEl || this.config.showFreeDeliveryHeader === false) return;

    const total = deliveryMachine.lastTotalCents ?? 0;
    const threshold = deliveryMachine.lastThresholdCents ?? 0;
    const remaining = Math.max(0, threshold - total);
    const amountText = formatMoneyNoDecimals(remaining);
    const amountHtml = `<strong>${amountText}</strong>`;
    const belowSub = replaceAmountPlaceholder(
      this.config.headerSubtext || "",
      amountHtml,
    );
    const postSub = replaceAmountPlaceholder(
      this.config.postSubtext || "",
      amountHtml,
    );

    if (this.titleBelow)
      this.titleBelow.textContent = this.config.headerTitle || "";
    if (this.subtextBelow) this.subtextBelow.innerHTML = belowSub;
    if (this.titlePost)
      this.titlePost.textContent = this.config.postTitle || "";
    if (this.subtextPost) this.subtextPost.innerHTML = postSub;
    if (this.congratsTitle)
      this.congratsTitle.textContent = this.config.congratsTitle || "";
    if (this.congratsMessage) {
      this.congratsMessage.textContent = this.config.congratsMessage || "";
    }

    const state = deliveryMachine.state;
    const showBelow = state === DeliveryState.BELOW_THRESHOLD;
    const showCongrats = state === DeliveryState.CONGRATULATIONS_VISIBLE;
    const showPost = state === DeliveryState.POST_CONGRATULATIONS;

    this.root.dataset.fbtDelivery = showPost ? "unlocked" : "below";
    this.toggleConfetti(showCongrats);

    this.togglePanel(this.panelBelow, showBelow);
    this.togglePanel(this.panelCongrats, showCongrats);
    this.togglePanel(this.panelPost, showPost);
  }

  toggleConfetti(show) {
    if (!this.confettiEl) return;
    const wasHidden =
      this.confettiEl.hasAttribute("hidden") || this.confettiEl.hidden;
    this.confettiEl.hidden = !show;
    this.confettiEl.setAttribute("aria-hidden", show ? "false" : "true");
    if (!this.confettiImg) return;
    if (show) {
      const src = this.confettiImg.getAttribute("data-src") || FBT_CONFETTI_URL;
      // Load/restart GIF only when entering congratulations (avoid flicker on re-render).
      if (wasHidden || !this.confettiImg.getAttribute("src")) {
        this.confettiImg.setAttribute("src", src);
      }
    } else if (this.confettiImg.getAttribute("src")) {
      this.confettiImg.removeAttribute("src");
    }
  }

  togglePanel(panel, visible) {
    if (!panel) return;
    const wasHidden = panel.hasAttribute("hidden");
    if (visible) {
      panel.removeAttribute("hidden");
      if (wasHidden) {
        panel.classList.add("is-enter");
        requestAnimationFrame(() => {
          panel.classList.add("is-enter-active");
          panel.classList.remove("is-enter");
          setTimeout(() => panel.classList.remove("is-enter-active"), 400);
        });
      }
    } else {
      panel.setAttribute("hidden", "");
      panel.classList.remove("is-enter", "is-enter-active");
    }
  }

  renderLoadingIfNeeded() {
    if (!this.trackEl) return;
    if (this.activeTab !== "dynamic") return;
    if (this.dynamicProducts.length) return;
    this.trackEl.innerHTML = `
      <div class="fbt-recommendation__skeleton" aria-hidden="true">
        <div class="fbt-recommendation__skeleton-card"></div>
        <div class="fbt-recommendation__skeleton-card"></div>
        <div class="fbt-recommendation__skeleton-card"></div>
      </div>
    `;
  }

  renderActiveProducts(cart) {
    if (!this.trackEl) return;
    const list =
      this.activeTab === "manual"
        ? this.getManualFiltered(cart || { items: [] })
        : this.getDynamicFiltered(cart || { items: [] });

    if (this.activeTab === "dynamic" && this.loadingDynamic && !list.length) {
      this.renderLoadingIfNeeded();
      return;
    }

    if (this.activeTab === "manual") {
      if (list.length) {
        const addLabel = this.config.addButtonLabel || "Add";
        this.trackEl.innerHTML = list
          .map((p) => this.cardHtml(p, addLabel))
          .join("");
        return;
      }
      if (this.manualTemplate?.content?.childElementCount) {
        this.trackEl.replaceChildren(
          ...this.manualTemplate.content.cloneNode(true).childNodes,
        );
        return;
      }
      this.trackEl.innerHTML = "";
      return;
    }

    if (!list.length) {
      this.trackEl.innerHTML = "";
      return;
    }

    const addLabel = this.config.addButtonLabel || "Add";
    this.trackEl.innerHTML = list
      .map((p) => this.cardHtml(p, addLabel))
      .join("");
  }

  cardHtml(product, addLabel) {
    const priceHtml = product.priceHtml || buildCardPriceHtml(product);
    const type = product.type
      ? `<div class="fbt-recommendation__card-type">${escapeHtml(product.type)}</div>`
      : `<div class="fbt-recommendation__card-type">&nbsp;</div>`;
    const img = product.image
      ? `<img src="${escapeAttr(product.image)}" alt="${escapeAttr(product.imageAlt || product.title)}" loading="lazy" width="160" height="160">`
      : "";
    const disabled =
      !product.available || !product.variantId ? " disabled" : "";

    return `
      <article class="fbt-recommendation__card" data-fbt-product-id="${escapeAttr(product.id)}">
        ${type}
        <a class="fbt-recommendation__card-media" href="${escapeAttr(product.url)}" tabindex="-1" aria-hidden="true">${img}</a>
        <a class="fbt-recommendation__card-title" href="${escapeAttr(product.url)}">${escapeHtml(product.title)}</a>
        <div class="fbt-recommendation__card-price">${priceHtml}</div>
        <button type="button" class="fbt-recommendation__add hdt-relative hdt-oh hdt-btn-loading__svg" data-fbt-add="${escapeAttr(String(product.variantId || ""))}" data-fbt-sku="${escapeAttr(product.sku || "")}" aria-label="${escapeAttr(addLabel + ": " + product.title)}"${disabled}><span class="hdt-btn-atc_text">${escapeHtml(addLabel)}</span><span class="hdt-loading__spinner hdt-absolute" hidden><svg width="16" height="16" class="hdt-svg-spinner" focusable="false" role="presentation" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="hdt-path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></span></button>
      </article>
    `;
  }

  getActiveTabLabel() {
    if (this.activeTab === "manual") {
      return this.config.manualTabLabel || "Buy Again";
    }
    return this.config.dynamicTabLabel || "Frequently bought together";
  }

  onTabClick(event) {
    const btn = event.currentTarget;
    const tab = btn?.getAttribute("data-fbt-tab");
    if (!tab || tab === this.activeTab) return;
    this.userPickedTab = true;
    this.activeTab = tab;
    this.renderTabs(
      this.tabDynamic && !this.tabDynamic.hidden,
      this.tabManual && !this.tabManual.hidden,
    );
    this.renderActiveProducts(this.lastCart);
  }

  onTabKeydown(event) {
    const key = event.key;
    if (
      key !== "ArrowLeft" &&
      key !== "ArrowRight" &&
      key !== "Home" &&
      key !== "End"
    ) {
      return;
    }
    const tabs = [
      ...(this.tabsEl?.querySelectorAll("[data-fbt-tab]:not([hidden])") || []),
    ];
    if (tabs.length < 2) return;
    event.preventDefault();
    const currentIndex = tabs.findIndex(
      (t) => t.getAttribute("aria-selected") === "true",
    );
    let nextIndex = currentIndex;
    if (key === "ArrowLeft")
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (key === "ArrowRight") nextIndex = (currentIndex + 1) % tabs.length;
    if (key === "Home") nextIndex = 0;
    if (key === "End") nextIndex = tabs.length - 1;
    const next = tabs[nextIndex];
    if (!next) return;
    next.focus();
    next.click();
  }

  async onProductsClick(event) {
    const btn = event.target.closest("[data-fbt-add]");
    if (!btn || btn.disabled) return;
    const variantId = btn.getAttribute("data-fbt-add");
    if (!variantId) return;

    trackFbtAdd(
      btn.getAttribute("data-fbt-sku") || "",
      this.getActiveTabLabel(),
    );

    btn.classList.add("is-loading");
    btn.setAttribute("aria-busy", "true");
    btn.disabled = true;
    try {
      await addVariantToCart(variantId, btn);
    } catch (err) {
      btn.disabled = false;
      if (
        typeof Shopify !== "undefined" &&
        typeof Shopify.cartNotification === "function"
      ) {
        Shopify.cartNotification(err?.message || "Unable to add to cart", true);
      }
    } finally {
      btn.classList.remove("is-loading");
      btn.removeAttribute("aria-busy");
    }
  }
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function escapeAttr(value) {
  return escapeHtml(value).replace(/`/g, "&#96;");
}

function cleanupDetachedInstances() {
  for (const instance of [...instances]) {
    if (!instance.root.isConnected) instance.destroy();
  }
}

function getFbtRootConfig(root) {
  const configEl = root?.querySelector?.("[data-fbt-config]");
  return configEl ? safeParseJson(configEl.textContent) : null;
}

// Flag-Y (CDP show_cart_fbt): true enables, false disables, null/missing means
// the A/B is not live. Only an explicit true counts as enabled.
function isCdpFbtEnabled(flag) {
  if (flag == null) return false;
  if (flag === true || flag === 1) return true;
  if (flag === false || flag === 0) return false;
  if (typeof flag === "string") {
    const normalized = flag.toLowerCase().trim();
    if (normalized === "true" || normalized === "1") return true;
    if (normalized === "false" || normalized === "0" || normalized === "null") {
      return false;
    }
  }
  return false;
}

// Flag-X is the theme checkbox (fbt_enable), on unless a PM turns it off.
// The block is omitted from the page when Flag-X is off; this still checks it.
function isThemeFbtEnabled(config) {
  if (config && config.themeEnabled === false) return false;
  return true;
}

// fbt_ab, same role as partnership_ab. True while the test is live.
// Set fbt_ab to false after the test to keep FBT without the CDP flag.
function isFbtAbEnabled(config) {
  if (!config || config.abEnabled == null) return true;
  return config.abEnabled !== false;
}

// Flag-X off → hide.
// fbt_ab off → show for everyone.
// fbt_ab on → CDP true variant only.
// Same rule for cart drawer and cart page.
function isCartFbtEnabledByExperiment(root) {
  const config = getFbtRootConfig(root);
  if (!isThemeFbtEnabled(config)) return false;
  if (!isFbtAbEnabled(config)) return true;
  const flagY = window.Shopify?.abExperimentData
    ? window.Shopify.abExperimentData.show_cart_fbt
    : null;
  return isCdpFbtEnabled(flagY);
}

function removeCartFbtRoots(scope = document) {
  const roots = scope.querySelectorAll
    ? scope.querySelectorAll("[data-fbt-recommendation]")
    : [];
  roots.forEach((root) => {
    if (isCartFbtEnabledByExperiment(root)) return;
    for (const instance of instances) {
      if (instance.root === root) {
        instance.destroy();
        break;
      }
    }
    root.remove();
  });
}

function initFbtRoots(scope = document) {
  removeCartFbtRoots(scope);

  const roots = scope.querySelectorAll
    ? scope.querySelectorAll(
        '[data-fbt-recommendation]:not([data-fbt-ready="1"])',
      )
    : [];
  roots.forEach((root) => {
    if (!isCartFbtEnabledByExperiment(root)) return;
    try {
      new FbtRecommendation(root).init();
    } catch (err) {
      console.warn("[FBT] init failed", err);
    }
  });
}

async function getLatestCart(eventCart) {
  if (eventCart && Array.isArray(eventCart.items)) return eventCart;
  try {
    if (typeof window.createCartPromise === "function") {
      return await window.createCartPromise(true);
    }
    const root = window.Shopify?.routes?.root || "/";
    const res = await fetch(`${root}cart.js`);
    if (res.ok) return await res.json();
  } catch {
    /* ignore */
  }
  return eventCart || null;
}

async function handleCartChange(event) {
  const token = ++cartSyncToken;
  // Wait for drawer/page section HTML replace (runs on same event).
  await new Promise((resolve) => setTimeout(resolve, 50));
  if (token !== cartSyncToken) return;

  cleanupDetachedInstances();
  initFbtRoots(document);

  const cart = await getLatestCart(event?.detail?.cart);
  if (!cart || token !== cartSyncToken) return;

  for (const instance of instances) {
    try {
      const configEl = instance.root.querySelector("[data-fbt-config]");
      const fresh = configEl ? safeParseJson(configEl.textContent) : null;
      if (fresh?.manualProducts) {
        instance.manualProducts = dedupeProducts(
          fresh.manualProducts.map(normalizeLiquidProduct).filter(Boolean),
        );
        instance.config.manualProducts = fresh.manualProducts;
      }
      if (fresh?.freeDeliveryThresholdCents != null) {
        instance.config.freeDeliveryThresholdCents =
          fresh.freeDeliveryThresholdCents;
      }
      instance.config.cart = {
        items: cart.items || [],
        total_price: cart.total_price,
      };
      await instance.refreshFromCart(cart, { forceDynamic: false });
    } catch (err) {
      console.warn("[FBT] cart refresh failed", err);
    }
  }
}

async function handleDrawerRender() {
  const token = ++cartSyncToken;
  cleanupDetachedInstances();
  initFbtRoots(document);

  const cart = await getLatestCart(null);
  if (!cart || token !== cartSyncToken) return;

  for (const instance of instances) {
    try {
      const configEl = instance.root.querySelector("[data-fbt-config]");
      const fresh = configEl ? safeParseJson(configEl.textContent) : null;
      if (fresh?.manualProducts) {
        instance.manualProducts = dedupeProducts(
          fresh.manualProducts.map(normalizeLiquidProduct).filter(Boolean),
        );
      }
      await instance.refreshFromCart(cart, { forceDynamic: false });
    } catch (err) {
      console.warn("[FBT] drawer render refresh failed", err);
    }
  }
}

function bindGlobalListeners() {
  if (listenersBound) return;
  listenersBound = true;
  document.addEventListener("cart:change", handleCartChange);
  document.addEventListener("cart:drawer:render", handleDrawerRender);
  document.addEventListener("cart:page:render", handleDrawerRender);
  document.addEventListener("cart:refresh", handleDrawerRender);
}

function boot() {
  bindGlobalListeners();
  initFbtRoots(document);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot, { once: true });
} else {
  boot();
}
